import momentTimezone from "moment-timezone";

const TOKEN_KEY = "BidWebToken";
const USER_DATA_KEY = "userData";
const SESSION_SCOPE_KEY = "BidWebAuthScope";
const SESSION_SCOPE = "session";
const SUPPORT_ACCESS_EXPIRY_KEY = "BidWebSupportAccessExpiry";
const SUPPORT_ACCESS_DURATION_MS = 10 * 60 * 1000;

const parseUserData = (value) => {
  return value ? JSON.parse(value) : null;
};

const getCurrentAuthScope = () => {
  return sessionStorage.getItem(SESSION_SCOPE_KEY);
};

const isSessionScopedAuth = () => {
  return getCurrentAuthScope() === SESSION_SCOPE;
};

const clearSessionScopeIfEmpty = () => {
  if (!sessionStorage.getItem(TOKEN_KEY) && !sessionStorage.getItem(USER_DATA_KEY)) {
    sessionStorage.removeItem(SESSION_SCOPE_KEY);
  }
};

export const getToken = () => {
  if (isSessionScopedAuth()) {
    return sessionStorage.getItem(TOKEN_KEY);
  }

  return localStorage.getItem(TOKEN_KEY);
};

export const removeToken = () => {
  if (isSessionScopedAuth()) {
    sessionStorage.removeItem(TOKEN_KEY);
    clearSessionScopeIfEmpty();
    return;
  }

  return localStorage.removeItem(TOKEN_KEY);
};

export const setToken = (token, scope = "local") => {
  if (scope === SESSION_SCOPE) {
    sessionStorage.setItem(SESSION_SCOPE_KEY, SESSION_SCOPE);
    return sessionStorage.setItem(TOKEN_KEY, token);
  }

  clearSessionAuth();
  return localStorage.setItem(TOKEN_KEY, token);
};

export const setUserData = (data) => {
  return localStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
};

export const getUserData = () => {
  if (isSessionScopedAuth()) {
    return parseUserData(sessionStorage.getItem(USER_DATA_KEY));
  }

  return parseUserData(localStorage.getItem(USER_DATA_KEY));
};
export const removeUserData = () => {
  if (isSessionScopedAuth()) {
    sessionStorage.removeItem(USER_DATA_KEY);
    clearSessionScopeIfEmpty();
    return;
  }

  return localStorage.removeItem(USER_DATA_KEY);
};

export const isImpersonatingUser = () => {
  return isSessionScopedAuth();
};

export const setSessionUserData = (data) => {
  sessionStorage.setItem(SESSION_SCOPE_KEY, SESSION_SCOPE);
  return sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(data));
};

export const startTabUserSession = ({ token, userData }) => {
  setToken(token, SESSION_SCOPE);
  setSessionUserData(userData);
  sessionStorage.setItem(
    SUPPORT_ACCESS_EXPIRY_KEY,
    String(Date.now() + SUPPORT_ACCESS_DURATION_MS)
  );
};

export const clearSessionAuth = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_DATA_KEY);
  sessionStorage.removeItem(SESSION_SCOPE_KEY);
  sessionStorage.removeItem(SUPPORT_ACCESS_EXPIRY_KEY);
};

export const getSupportAccessExpiry = () => {
  const value = sessionStorage.getItem(SUPPORT_ACCESS_EXPIRY_KEY);
  return value ? Number(value) : null;
};

export const getSupportAccessRemainingMs = () => {
  const expiry = getSupportAccessExpiry();

  if (!expiry) {
    return 0;
  }

  return Math.max(expiry - Date.now(), 0);
};

export const openAccessAccountTab = ({ token, userData, path = "/dashboard" }) => {
  const supportWindow = window.open("", "_blank");

  if (!supportWindow) {
    return false;
  }

  supportWindow.sessionStorage.setItem(SESSION_SCOPE_KEY, SESSION_SCOPE);
  supportWindow.sessionStorage.setItem(TOKEN_KEY, token);
  supportWindow.sessionStorage.setItem(
    USER_DATA_KEY,
    JSON.stringify(userData)
  );
  supportWindow.sessionStorage.setItem(
    SUPPORT_ACCESS_EXPIRY_KEY,
    String(Date.now() + SUPPORT_ACCESS_DURATION_MS)
  );
  supportWindow.location.href = `${window.location.origin}/#${path}`;
  return true;
};

export const timeZones = () => {
  return momentTimezone.tz.names();
};

export const localSetTimeZone = () => {
  return localStorage.setItem("localTimeZone", momentTimezone.tz.guess());
};

export const localTimeZone = () => {
  return localStorage.getItem("localTimeZone");
};

export const removeLocalTimeZone = () => {
  return localStorage.removeItem("localTimeZone");
};

export const getTimeZone = () => {
  return localStorage.getItem("currentTimeZone");
};
